import { forwardRef, useImperativeHandle, useState } from "react";
import useSWR from "swr";
import { Alert, Anchor, Button, Group, Select, SegmentedControl, Stack, Switch, Text, TextInput } from "@mantine/core";
import {
  attachSubjectPlaylist, detachSubjectPlaylist, getChannels, getSubjects, parseApiError, playlistUrl, useFetch,
  Channel, Platform, PLATFORM_VK, PLATFORM_YOUTUBE, SubjectPlaylist,
} from "../../../shared/api";
import { PLATFORM_TITLES, usePlatformLogin } from "../../../shared/platformLogin";

const PLATFORMS: Platform[] = [PLATFORM_YOUTUBE, PLATFORM_VK];

type Mode = "keep" | "create" | "link" | "unlink";
interface Draft { mode: Mode; channel: string | null; title: string; titleTouched: boolean; isPublic: boolean; playlistId: string | null }
interface Duplicate { playlist_id: string; title: string; url: string }
interface RowState { error?: string; duplicate?: Duplicate; busy?: boolean }

const EMPTY_DRAFT: Draft = { mode: "keep", channel: null, title: "", titleTouched: false, isPublic: true, playlistId: null };

export interface SubjectPlaylistsHandle {
  /** Applies pending create/link/unlink actions; resolves to false if any platform failed. */
  apply: (subjectId: number) => Promise<boolean>;
}

export function useChannels() {
  const { fetch } = useFetch();
  return useSWR("playlists/channels", () => getChannels({ fetch }));
}

export function playlistTitle(channels: { vk: Channel[]; youtube: Channel[] } | undefined, l: SubjectPlaylist): string {
  return channels?.[l.platform]?.find(c => c.id === l.channel_id)?.playlists.find(p => p.playlist_id === l.playlist_id)?.title ?? l.playlist_id;
}

/**
 * The "Плейлисты" block of the subject form: one row per platform. Actions are
 * collected as drafts and applied by the parent's "Сохранить" (after the subject
 * itself is saved), so the same block serves both creating and editing a subject.
 */
export const SubjectPlaylists = forwardRef<SubjectPlaylistsHandle, { subjectId: number | null; subjectTitle: string }>(
  function SubjectPlaylists({ subjectId, subjectTitle }, ref) {
    const { fetch } = useFetch();
    const { data: subjects, mutate: mutateSubjects } = useSWR("admin/subjects", () => getSubjects({ fetch }));
    const { data: channels, mutate: mutateChannels } = useChannels();
    const [drafts, setDrafts] = useState<Record<string, Draft>>({});
    const [rows, setRows] = useState<Record<string, RowState>>({});
    const [lastSubjectId, setLastSubjectId] = useState<number | null>(subjectId);

    const sid = subjectId ?? lastSubjectId;
    const linked = (p: Platform) => subjects?.find(s => s.id === sid)?.playlists?.find(l => l.platform === p) ?? null;
    const draft = (p: Platform): Draft => {
      const d = drafts[p] ?? EMPTY_DRAFT;
      return d.titleTouched ? d : { ...d, title: subjectTitle };
    };
    const patchDraft = (p: Platform, patch: Partial<Draft>) => setDrafts(ds => ({ ...ds, [p]: { ...(ds[p] ?? EMPTY_DRAFT), ...patch } }));
    const setRow = (p: Platform, r: RowState) => setRows(rs => ({ ...rs, [p]: r }));

    async function attach(p: Platform, id: number, req: Parameters<typeof attachSubjectPlaylist>[0]["req"]): Promise<boolean> {
      setRow(p, { busy: true });
      try {
        await attachSubjectPlaylist({ subjectId: id, platform: p, req, fetch });
        setRow(p, {}); setDrafts(ds => ({ ...ds, [p]: EMPTY_DRAFT }));
        return true;
      } catch (e) {
        const err = parseApiError(e);
        if (err.status === 409 && err.detail?.playlist_id) setRow(p, { duplicate: err.detail });
        else setRow(p, { error: err.message });
        return false;
      }
    }

    async function applyOne(p: Platform, id: number): Promise<boolean> {
      const d = draft(p);
      if (d.mode === "keep") return true;
      if (d.mode === "unlink") {
        try { await detachSubjectPlaylist({ subjectId: id, platform: p, fetch }); setDrafts(ds => ({ ...ds, [p]: EMPTY_DRAFT })); setRow(p, {}); return true; }
        catch (e) { setRow(p, { error: parseApiError(e).message }); return false; }
      }
      if (!d.channel) { setRow(p, { error: "Выберите канал" }); return false; }
      if (d.mode === "link") {
        if (!d.playlistId) { setRow(p, { error: "Выберите плейлист" }); return false; }
        return attach(p, id, { channel_id: d.channel, playlist_id: d.playlistId });
      }
      if (!d.title.trim()) { setRow(p, { error: "Укажите название плейлиста" }); return false; }
      return attach(p, id, { channel_id: d.channel, title: d.title.trim(), public: p === PLATFORM_VK ? true : d.isPublic });
    }

    useImperativeHandle(ref, () => ({
      apply: async (id: number) => {
        setLastSubjectId(id);
        // Sequential on purpose: errors are shown per platform, and one failing must not stop the other.
        let ok = true;
        for (const p of PLATFORMS) ok = (await applyOne(p, id)) && ok;
        await Promise.all([mutateSubjects(), mutateChannels()]);
        return ok;
      },
    }));

    async function resolveDuplicate(p: Platform, dup: Duplicate, force: boolean) {
      if (sid == null) return;
      const d = draft(p);
      const ok = await attach(p, sid, force
        ? { channel_id: d.channel!, title: d.title.trim(), public: p === PLATFORM_VK ? true : d.isPublic, force: true }
        : { channel_id: d.channel!, playlist_id: dup.playlist_id });
      if (ok) await Promise.all([mutateSubjects(), mutateChannels()]);
    }

    return (
      <Stack gap="xs">
        <Text fw={500} fz="sm">Плейлисты</Text>
        {PLATFORMS.map(p => (
          <PlatformRow
            key={p} platform={p} channels={channels?.[p] ?? []} linked={linked(p)} draft={draft(p)} row={rows[p] ?? {}}
            onDraft={patch => { patchDraft(p, patch); setRow(p, {}); }}
            onResolveDuplicate={(dup, force) => resolveDuplicate(p, dup, force)}
            linkedTitle={l => playlistTitle(channels, l)}
          />
        ))}
      </Stack>
    );
  },
);

function PlatformRow({ platform, channels, linked, draft, row, onDraft, onResolveDuplicate, linkedTitle }: {
  platform: Platform; channels: Channel[]; linked: SubjectPlaylist | null; draft: Draft; row: RowState;
  onDraft: (patch: Partial<Draft>) => void; onResolveDuplicate: (dup: Duplicate, force: boolean) => void;
  linkedTitle: (l: SubjectPlaylist) => string;
}) {
  const name = PLATFORM_TITLES[platform];
  const login = usePlatformLogin(platform, draft.mode === "create");
  const channelOptions = channels.map(c => ({ value: c.id, label: c.description }));
  const channel = draft.channel ?? (channels.length === 1 ? channels[0].id : null);
  const setMode = (mode: Mode) => onDraft({ mode, channel });
  const playlistOptions = (channels.find(c => c.id === channel)?.playlists ?? []).map(pl => ({ value: pl.playlist_id, label: pl.title }));

  let body;
  if (linked && draft.mode !== "unlink") {
    const url = playlistUrl(platform, linked.channel_id, linked.playlist_id);
    body = (
      <Group justify="space-between" wrap="nowrap">
        <Anchor href={url} target="_blank" rel="noopener noreferrer" fz="sm" truncate>{linkedTitle(linked)}</Anchor>
        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => onDraft({ mode: "unlink" })}>Отвязать</Button>
      </Group>
    );
  } else if (linked) {
    body = (
      <Group justify="space-between" wrap="nowrap">
        <Text fz="sm" c="dimmed">Будет отвязан при сохранении (на платформе останется)</Text>
        <Button size="compact-xs" variant="subtle" color="gray" onClick={() => onDraft({ mode: "keep" })}>Отмена</Button>
      </Group>
    );
  } else {
    body = (
      <Stack gap={6}>
        <SegmentedControl size="xs" value={draft.mode} onChange={v => setMode(v as Mode)}
          data={[{ value: "keep", label: "Нет" }, { value: "create", label: "Создать" }, { value: "link", label: "Выбрать существующий" }]} />
        {draft.mode !== "keep" && (
          <Select size="xs" placeholder="Канал" data={channelOptions} value={channel} onChange={v => onDraft({ channel: v, playlistId: null })} />
        )}
        {draft.mode === "create" && (
          <>
            <TextInput size="xs" placeholder="Название плейлиста" value={draft.title} onChange={e => onDraft({ title: e.target.value, titleTouched: true })} />
            {platform === PLATFORM_YOUTUBE
              ? <Switch size="xs" label="Публичный (иначе — доступ по ссылке)" checked={draft.isPublic} onChange={e => onDraft({ isPublic: e.currentTarget.checked })} />
              : <Text fz="xs" c="dimmed">В ВК плейлист открыт так же, как группа</Text>}
            {login.ok === false && (
              <Text fz="xs" c="orange">Нет авторизации в {name}. <Anchor component="button" type="button" fz="xs" onClick={login.login}>Войти</Anchor></Text>
            )}
          </>
        )}
        {draft.mode === "link" && (
          <Select size="xs" placeholder="Плейлист" searchable data={playlistOptions} value={draft.playlistId} onChange={v => onDraft({ playlistId: v })}
            nothingFoundMessage={channel ? "Нет плейлистов — обновите их на странице «Плейлисты»" : "Сначала выберите канал"} />
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={4} p="xs" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
      <Text fz="sm" fw={600}>{name}</Text>
      {body}
      {row.error && <Alert color="red" p="xs"><Text fz="xs">{row.error}</Text></Alert>}
      {row.duplicate && (
        <Alert color="yellow" p="xs">
          <Text fz="xs" mb={6}>На канале уже есть плейлист <Anchor href={row.duplicate.url} target="_blank" rel="noopener noreferrer" fz="xs">«{row.duplicate.title}»</Anchor>.</Text>
          <Group gap="xs">
            <Button size="compact-xs" loading={row.busy} onClick={() => onResolveDuplicate(row.duplicate!, false)}>Привязать его</Button>
            <Button size="compact-xs" variant="default" loading={row.busy} onClick={() => onResolveDuplicate(row.duplicate!, true)}>Всё равно создать новый</Button>
          </Group>
        </Alert>
      )}
    </Stack>
  );
}
